/**
 * WebSocket Event Constants
 *
 * Centralized, type-safe event definitions for all WebSocket communication.
 * Organized by domain namespace for clarity and maintainability.
 *
 * Naming Convention:
 * - Client → Server (commands): Present tense imperatives (create, join, update)
 * - Server → Client (events): Past tense notifications (created, joined, updated)
 * - Pattern: namespace:resource:action or namespace:action
 *
 * Usage:
 * - Backend: server.emit(MessageEvents.STREAMING, payload)
 * - Frontend: socket.on(MessageEvents.STREAMING, handler)
 *
 * All constants use `as const` for literal type inference
 */

/**
 * Connection lifecycle events (Socket.IO built-in + custom)
 *
 * @example Backend (NestJS)
 * ```typescript
 * client.emit(ConnectionEvents.READY, { userId, clientId: client.id });
 * ```
 *
 * @example Frontend (React)
 * ```typescript
 * socket.on(ConnectionEvents.READY, (data) => {
 *   console.log('Connected:', data.userId);
 * });
 * ```
 */
export const ConnectionEvents = {
  /** Socket.IO built-in event - fires when connection is established */
  CONNECT: "connect",

  /** Socket.IO built-in event - fires when connection is lost */
  DISCONNECT: "disconnect",

  /** Socket.IO built-in event - fires when connection attempt fails */
  CONNECT_ERROR: "connect_error",

  /**
   * Server → Client: Emitted after successful connection with user/client details
   * Payload: { userId: string, clientId: string }
   */
  READY: "connection:ready",

  /**
   * Server → Client: Emitted when a connection-level error occurs
   * Payload: { message: string, code?: string }
   */
  ERROR: "connection:error"
} as const;

/**
 * Chat room management events
 * Client sends commands (join/leave), server sends confirmations (joined/left)
 *
 * @example Backend - Handle room join
 * ```typescript
 * @SubscribeMessage(ChatRoomEvents.JOIN)
 * async handleJoinChat(@MessageBody() payload: JoinChatPayload) {
 *   await client.join(`chat:${payload.chatId}`);
 *   client.emit(ChatRoomEvents.JOINED, { chatId: payload.chatId, room: roomName });
 * }
 * ```
 *
 * @example Frontend - Join and listen
 * ```typescript
 * socket.emit(ChatRoomEvents.JOIN, { chatId: 'abc123' });
 * socket.on(ChatRoomEvents.JOINED, ({ chatId, room }) => {
 *   console.log(`Joined chat ${chatId} in room ${room}`);
 * });
 * ```
 */
export const ChatRoomEvents = {
  /**
   * Client → Server: Request to join a chat room
   * Payload: { chatId: string }
   */
  JOIN: "chat:room:join",

  /**
   * Client → Server: Request to leave a chat room
   * Payload: { chatId: string }
   */
  LEAVE: "chat:room:leave",

  /**
   * Server → Client: Confirmation that client successfully joined a chat room
   * Payload: { chatId: string, room: string }
   */
  JOINED: "chat:room:joined",

  /**
   * Server → Client: Confirmation that client successfully left a chat room
   * Payload: { chatId: string, room: string }
   */
  LEFT: "chat:room:left"
} as const;

/**
 * Message lifecycle events
 * Covers creation, streaming, and completion
 *
 * @example Backend - Stream AI response
 * ```typescript
 * // User message received
 * this.server.to(roomName).emit(MessageEvents.CREATED, {
 *   messageId, content, timestamp: Date.now()
 * });
 *
 * // Stream AI tokens
 * for (const token of aiResponse) {
 *   this.server.to(roomName).emit(MessageEvents.STREAMING, {
 *     messageId, token, timestamp: Date.now()
 *   });
 * }
 *
 * // Complete message
 * this.server.to(roomName).emit(MessageEvents.COMPLETED, {
 *   messageId, content: fullContent, timestamp: Date.now()
 * });
 * ```
 *
 * @example Frontend - Display streaming message
 * ```typescript
 * socket.on(MessageEvents.STREAMING, ({ messageId, token }) => {
 *   setMessages(prev => ({
 *     ...prev,
 *     [messageId]: { content: (prev[messageId]?.content || '') + token }
 *   }));
 * });
 *
 * socket.on(MessageEvents.COMPLETED, ({ messageId, content }) => {
 *   dispatch(addMessage({ id: messageId, content }));
 * });
 * ```
 */
export const MessageEvents = {
  /**
   * Client → Server: Request to create a new message
   * Payload: { chatId: string, content: string }
   */
  CREATE: "message:create",

  /**
   * Server → Client: User message was created and saved
   * Payload: { messageId: string, content: string, timestamp: number }
   * Emitted immediately when a user sends a message
   */
  CREATED: "message:created",

  /**
   * Server → Client: AI response token being streamed
   * Payload: { messageId: string, token: string, timestamp: number }
   * Emitted multiple times during AI response generation
   */
  STREAMING: "message:streaming",

  /**
   * Server → Client: Message streaming completed with full content
   * Payload: { messageId: string, content: any, timestamp: number }
   * Emitted once when AI response is complete
   */
  COMPLETED: "message:completed",

  /**
   * Server → Client: Message processing failed with error details
   * Payload: { messageId: string, error: { code: string, message: string }, timestamp: number }
   */
  FAILED: "message:failed"
} as const;

/**
 * Chat metadata events
 * Real-time updates for chat properties and participants
 *
 * @example Backend - Update chat topic
 * ```typescript
 * this.server.to(`chat:${chatId}`).emit(ChatMetadataEvents.TOPIC_UPDATED, {
 *   chatId, topic: 'New Topic', timestamp: Date.now()
 * });
 * ```
 *
 * @example Frontend - Listen for metadata changes
 * ```typescript
 * socket.on(ChatMetadataEvents.TOPIC_UPDATED, ({ chatId, topic }) => {
 *   dispatch(updateChatTopic({ chatId, topic }));
 * });
 * ```
 */
export const ChatMetadataEvents = {
  /**
   * Server → Client: New chat was created
   * Payload: { chat: any, timestamp: number }
   */
  CREATED: "chat:created",

  /**
   * Server → Client: Chat properties were updated
   * Payload: { chatId: string, updates: any, timestamp: number }
   */
  UPDATED: "chat:updated",

  /**
   * Server → Client: Chat was deleted
   * Payload: { chatId: string, timestamp: number }
   */
  DELETED: "chat:deleted",

  /**
   * Server → Client: Chat topic/title was updated
   * Payload: { chatId: string, topic: string, timestamp: number }
   */
  TOPIC_UPDATED: "chat:topic:updated",

  /**
   * Server → Client: Chat status changed (e.g., open, closed, archived)
   * Payload: { chatId: string, status: string, timestamp: number }
   */
  STATUS_UPDATED: "chat:status:updated",

  /**
   * Server → Client: New participant joined the chat
   * Payload: { chatId: string, userId: string, userName: string, timestamp: number }
   */
  PARTICIPANT_JOINED: "chat:participant:joined",

  /**
   * Server → Client: Participant left the chat
   * Payload: { chatId: string, userId: string, timestamp: number }
   */
  PARTICIPANT_LEFT: "chat:participant:left"
} as const;

/**
 * Workflow execution events (future use)
 * Real-time workflow execution progress and status updates
 *
 * @example Backend - Stream workflow progress
 * ```typescript
 * this.server.to(`workflow:${workflowId}`).emit(WorkflowEvents.STARTED, {
 *   workflowId, executionId, timestamp: Date.now()
 * });
 *
 * this.server.to(`workflow:${workflowId}`).emit(WorkflowEvents.PROGRESS, {
 *   workflowId, executionId, progress: 50, currentNode: 'node-123', timestamp: Date.now()
 * });
 * ```
 *
 * @example Frontend - Display workflow progress
 * ```typescript
 * socket.on(WorkflowEvents.PROGRESS, ({ progress, currentNode }) => {
 *   setWorkflowProgress({ percentage: progress, currentStep: currentNode });
 * });
 * ```
 */
export const WorkflowEvents = {
  /**
   * Client → Server: Request to join organization workflow room
   * Payload: { organizationId: string }
   */
  JOIN: "workflow:room:join",

  /**
   * Client → Server: Request to leave organization workflow room
   * Payload: { organizationId: string }
   */
  LEAVE: "workflow:room:leave",

  /**
   * Server → Client: Confirmation of successful workflow room join
   * Payload: { organizationId: string, room: string, timestamp: number }
   */
  JOINED: "workflow:room:joined",

  /**
   * Server → Client: Confirmation of successful workflow room leave
   * Payload: { organizationId: string, room: string, timestamp: number }
   */
  LEFT: "workflow:room:left",

  /**
   * Server → Client: Workflow execution has started
   * Payload: { workflowId: string, executionId: string, timestamp: number }
   */
  STARTED: "workflow:started",

  /**
   * Server → Client: Workflow execution progress update
   * Payload: { workflowId: string, executionId: string, progress: number, currentNode: string, timestamp: number }
   */
  PROGRESS: "workflow:progress",

  /**
   * Server → Client: Workflow execution completed successfully
   * Payload: { workflowId: string, executionId: string, result: any, timestamp: number }
   */
  COMPLETED: "workflow:completed",

  /**
   * Server → Client: Workflow execution failed
   * Payload: { workflowId: string, executionId: string, error: { code: string, message: string }, timestamp: number }
   */
  FAILED: "workflow:failed",

  /**
   * Server → Client: Workflow execution was cancelled
   * Payload: { workflowId: string, executionId: string, reason: string, timestamp: number }
   */
  CANCELLED: "workflow:cancelled",

  /**
   * Server → Client: Workflow node execution started
   * Payload: TBD - future implementation
   */
  NODE_STARTED: "workflow:node:started",

  /**
   * Server → Client: Workflow node execution completed
   * Payload: TBD - future implementation
   */
  NODE_COMPLETED: "workflow:node:completed",

  /**
   * Server → Client: Workflow node execution failed
   * Payload: TBD - future implementation
   */
  NODE_FAILED: "workflow:node:failed"
} as const;

/**
 * Inbox/notification events (future use)
 * Real-time inbox and notification updates for customer support workflows
 *
 * @example Backend - Notify new message
 * ```typescript
 * this.server.to(`user:${userId}`).emit(InboxEvents.NEW_MESSAGE, {
 *   chatId, messageId, preview: 'Hello, I need help...', timestamp: Date.now()
 * });
 * ```
 *
 * @example Frontend - Update inbox UI
 * ```typescript
 * socket.on(InboxEvents.NEW_MESSAGE, ({ chatId, messageId, preview }) => {
 *   dispatch(addInboxMessage({ chatId, messageId, preview }));
 *   playNotificationSound();
 * });
 *
 * socket.on(InboxEvents.UNREAD_COUNT, ({ count }) => {
 *   setUnreadCount(count);
 * });
 * ```
 */
/**
 * Organization room management events
 * Multi-tenant room subscriptions for inbox and playground views
 *
 * @example Backend - Handle organization room join
 * ```typescript
 * @SubscribeMessage(OrganizationEvents.JOIN)
 * async handleJoinOrganization(@MessageBody() payload: JoinOrganizationPayload) {
 *   const roomName = `org:${payload.organizationId}:${payload.type}`;
 *   await client.join(roomName);
 *   client.emit(OrganizationEvents.JOINED, { organizationId, type, room: roomName });
 * }
 * ```
 *
 * @example Frontend - Join organization room
 * ```typescript
 * socket.emit(OrganizationEvents.JOIN, { organizationId: 'org-123', type: 'inbox' });
 * socket.on(OrganizationEvents.JOINED, ({ organizationId, type, room }) => {
 *   console.log(`Joined ${type} room for org ${organizationId}`);
 * });
 * ```
 */
export const OrganizationEvents = {
  /**
   * Client → Server: Request to join an organization room
   * Payload: { organizationId: string, type?: "inbox" | "playground" }
   */
  JOIN: "organization:join",

  /**
   * Client → Server: Request to leave an organization room
   * Payload: { organizationId: string, type?: "inbox" | "playground" }
   */
  LEAVE: "organization:leave",

  /**
   * Server → Client: Confirmation of successful organization room join
   * Payload: { organizationId: string, type: "inbox" | "playground", room: string, timestamp: number }
   */
  JOINED: "organization:joined",

  /**
   * Server → Client: Confirmation of successful organization room leave
   * Payload: { organizationId: string, type: "inbox" | "playground", room: string, timestamp: number }
   */
  LEFT: "organization:left"
} as const;

/**
 * Conversations namespace — V2 inbox surface
 *
 * Surgical thread/message events broadcast on /conversations to org-scoped
 * rooms. Each event names a distinct cache mutation; frontend handlers patch
 * React Query caches via setQueryData rather than invalidate-and-refetch.
 *
 * @example Backend
 * ```typescript
 * server.to(`org:${orgId}:conversations`).emit(ConversationsEvents.THREAD_UPDATED, {
 *   organizationId: orgId,
 *   chatId
 * });
 * ```
 *
 * @example Frontend
 * ```typescript
 * socket.on(ConversationsEvents.THREAD_UPDATED, (p: ConversationsThreadEventPayload) => {
 *   queryClient.setQueryData(["conversations", "thread", p.chatId], (old) => ...);
 * });
 * ```
 */
export const ConversationsEvents = {
  /** Server → Client: a new chat surfaced in the org's inbox */
  THREAD_CREATED: "conversations:thread:created",
  /** Server → Client: a thread row changed (status, assignment, last message, etc.) */
  THREAD_UPDATED: "conversations:thread:updated",
  /** Server → Client: a thread was deleted; consumers must clear selection if active */
  THREAD_DELETED: "conversations:thread:deleted",
  /** Server → Client: a new message exists in a thread */
  MESSAGE_CREATED: "conversations:message:created",
  /** Server → Client: an existing message changed status / content (e.g. validation workflow) */
  MESSAGE_UPDATED: "conversations:message:updated",
  /**
   * Server → Client: per-user read-state changed for one thread.
   *
   * Broadcast on the per-user room `user:{userId}:conversations` ONLY —
   * never on the org room. `lastReadAt` is per-user state, so the org
   * surface can't know whose state changed; the per-user room reaches
   * exactly the tabs that share this user's identity (multi-tab sync).
   *
   * Payload: ConversationsReadStateUpdatedPayload — `lastReadAt` is an
   * ISO string for mark-as-read, `null` for mark-as-unread.
   */
  READ_STATE_UPDATED: "conversations:read-state:updated"
} as const;

export const InboxEvents = {
  /**
   * Server → Client: New message received in inbox
   * Payload: { chatId: string, messageId: string, preview: string, timestamp: number }
   */
  NEW_MESSAGE: "inbox:new:message",

  /**
   * Server → Client: Unread message count updated
   * Payload: { count: number, timestamp: number }
   */
  UNREAD_COUNT: "inbox:unread:count",

  /**
   * Server → Client: Chat was assigned to a user
   * Payload: { chatId: string, assignedTo: string, timestamp: number }
   */
  CHAT_ASSIGNED: "inbox:chat:assigned",

  /**
   * Server → Client: Chat assignment was removed
   * Payload: TBD - future implementation
   */
  CHAT_UNASSIGNED: "inbox:chat:unassigned",

  /**
   * Server → Client: Chat was marked as resolved
   * Payload: { chatId: string, resolvedBy: string, timestamp: number }
   */
  CHAT_RESOLVED: "inbox:chat:resolved"
} as const;

/**
 * Document lifecycle events
 * Real-time document processing status updates
 *
 * @example Backend - Emit document status
 * ```typescript
 * this.server.to(`org:${organizationId}:documents`).emit(DocumentEvents.STATUS_UPDATED, {
 *   documentId, status: 'READY', timestamp: Date.now()
 * });
 * ```
 *
 * @example Frontend - Listen for document updates
 * ```typescript
 * socket.on(DocumentEvents.STATUS_UPDATED, ({ documentId, status }) => {
 *   dispatch(updateDocumentStatus({ documentId, status }));
 * });
 * ```
 */
export const DocumentEvents = {
  /**
   * Client → Server: Request to join organization documents room
   * Payload: { organizationId: string }
   */
  JOIN: "document:room:join",

  /**
   * Client → Server: Request to leave organization documents room
   * Payload: { organizationId: string }
   */
  LEAVE: "document:room:leave",

  /**
   * Server → Client: Confirmation of successful documents room join
   * Payload: { organizationId: string, room: string, timestamp: number }
   */
  JOINED: "document:room:joined",

  /**
   * Server → Client: Confirmation of successful documents room leave
   * Payload: { organizationId: string, room: string, timestamp: number }
   */
  LEFT: "document:room:left",

  /**
   * Server → Client: Document was created
   * Payload: { document: any, timestamp: number }
   */
  CREATED: "document:created",

  /**
   * Server → Client: Document status was updated (e.g., PENDING → PROCESSING → READY)
   * Payload: { documentId: string, status: string, timestamp: number }
   */
  STATUS_UPDATED: "document:status:updated",

  /**
   * Server → Client: Document processing progress update
   * Payload: { documentId: string, progress: number, timestamp: number }
   */
  PROGRESS_UPDATED: "document:progress:updated",

  /**
   * Server → Client: Document was updated
   * Payload: { documentId: string, updates: any, timestamp: number }
   */
  UPDATED: "document:updated",

  /**
   * Server → Client: Document was deleted
   * Payload: { documentId: string, timestamp: number }
   */
  DELETED: "document:deleted",

  /**
   * Server → Client: Document processing failed
   * Payload: { documentId: string, error: { code: string, message: string }, timestamp: number }
   */
  FAILED: "document:failed"
} as const;

/**
 * Deployment events (future use)
 * Real-time deployment connection and sync status updates
 *
 * @example Backend - Notify deployment status
 * ```typescript
 * this.server.to(`org:${orgId}`).emit(DeploymentEvents.CONNECTED, {
 *   deploymentId, provider: 'slack', timestamp: Date.now()
 * });
 * ```
 *
 * @example Frontend - Display deployment status
 * ```typescript
 * socket.on(DeploymentEvents.CONNECTED, ({ deploymentId, provider }) => {
 *   dispatch(updateDeploymentStatus({ deploymentId, status: 'connected' }));
 *   showToast(`${provider} connected successfully`);
 * });
 * ```
 */
export const DeploymentEvents = {
  /**
   * Server → Client: Deployment successfully connected
   * Payload: TBD - future implementation
   */
  CONNECTED: "deployment:connected",

  /**
   * Server → Client: Deployment disconnected
   * Payload: TBD - future implementation
   */
  DISCONNECTED: "deployment:disconnected",

  /**
   * Server → Client: Deployment data sync in progress or completed
   * Payload: TBD - future implementation
   */
  DATA_SYNC: "deployment:data:sync",

  /**
   * Server → Client: Deployment error occurred
   * Payload: TBD - future implementation
   */
  ERROR: "deployment:error"
} as const;

/**
 * OAuth connection events
 * Real-time OAuth provider connection lifecycle events
 *
 * @example Backend - Notify connection created
 * ```typescript
 * this.documentsEvents.emitOAuthConnectionCreated(organizationId, 'notion');
 * ```
 *
 * @example Frontend - Listen for new connection
 * ```typescript
 * socket.on(OAuthConnectionEvents.CREATED, ({ provider }) => {
 *   if (provider === 'notion') {
 *     fetchTokenAndLoadContent();
 *   }
 * });
 * ```
 */
export const OAuthConnectionEvents = {
  /**
   * Client → Server: Request a valid token for a provider
   * Payload: OAuthTokenRequestPayload
   */
  TOKEN_REQUEST: "oauth:token:request",

  /**
   * Server → Client: Token response (ready | not_connected | needs_reauth)
   * Payload: OAuthTokenResponsePayload (discriminated union)
   */
  TOKEN_RESPONSE: "oauth:token:response",

  /**
   * Server → Client: OAuth connection successfully created after OAuth
   * Payload: OAuthConnectionCreatedPayload
   */
  CREATED: "oauth:connection:created",

  /**
   * Server → Client: OAuth connection was disconnected
   * Payload: OAuthConnectionDisconnectedPayload
   */
  DISCONNECTED: "oauth:connection:disconnected",

  /**
   * Server → Client: OAuth connection token was refreshed
   * Payload: OAuthTokenRefreshedPayload
   */
  TOKEN_REFRESHED: "oauth:connection:token:refreshed"
} as const;

/**
 * Provider handshake events
 * Real-time events for unified provider connection flows (OAuth, Slack install, Teams install)
 *
 * Sent to user's personal room after handshake completion.
 * Each payload includes `handshakeId` so frontend can match specific flows.
 *
 * @example Backend - Notify handshake completed
 * ```typescript
 * this.userEvents.emitToUser(userId, HandshakeEvents.COMPLETED, {
 *   handshakeId: handshake.id,
 *   type: HandshakeType.SLACK_APP_INSTALL,
 *   connectionId: connection.id,
 *   connectionType: 'API_KEY',
 *   timestamp: Date.now()
 * });
 * ```
 *
 * @example Frontend - Listen for handshake completion
 * ```typescript
 * socket.on(HandshakeEvents.COMPLETED, (payload) => {
 *   // Filter by handshakeId to match the flow this tab started
 *   if (payload.handshakeId === pendingHandshakeId) {
 *     toast.success(`Connected: ${payload.metadata?.teamName}`);
 *     refetchConnections();
 *   }
 * });
 * ```
 */
export const HandshakeEvents = {
  /**
   * Server → Client: Handshake completed successfully
   * Connection was created (OAuth or ApiKey depending on type)
   * Payload: HandshakeCompletedPayload
   */
  COMPLETED: "handshake:completed",

  /**
   * Server → Client: Handshake failed with error
   * Payload: HandshakeFailedPayload
   */
  FAILED: "handshake:failed"
} as const;

/**
 * File upload events
 * Real-time file upload progress and status updates
 *
 * @example Backend - Emit upload progress
 * ```typescript
 * this.server.to(`org:${orgId}:uploads`).emit(UploadEvents.PROGRESS, {
 *   attachmentId: 'att-123',
 *   loaded: 5000,
 *   total: 10000,
 *   percentage: 50,
 *   timestamp: Date.now()
 * });
 * ```
 *
 * @example Frontend - Listen for progress
 * ```typescript
 * socket.on(UploadEvents.PROGRESS, ({ attachmentId, percentage }) => {
 *   updateAttachmentProgress(attachmentId, percentage);
 * });
 * ```
 */
export const BrowserbaseEvents = {
  SANDBOX_CREATING: "browserbase:sandbox:creating",
  SANDBOX_CREATED: "browserbase:sandbox:created",
  DEPS_INSTALLING: "browserbase:deps:installing",
  DEPS_INSTALLED: "browserbase:deps:installed",
  EXECUTING: "browserbase:executing",
  COMPLETED: "browserbase:completed",
  FAILED: "browserbase:failed",
  /** Server → Client: Session was persisted to database */
  SESSION_CREATED: "browserbase:session:created"
} as const;

/**
 * User-scoped room events
 * Personal room for user-specific real-time events (OAuth, notifications, etc.)
 *
 * Room pattern: `user:{userId}`
 * - All tabs for the same user join this room
 * - Events are user-specific, not shared with other org members
 * - Perfect for OAuth callbacks, personal notifications
 *
 * @example Backend - Handle user room join
 * ```typescript
 * @SubscribeMessage(UserEvents.JOIN)
 * async handleJoinUser(
 *   @ConnectedSocket() client: Socket,
 *   @MessageBody() payload: JoinUserPayload
 * ) {
 *   const roomName = `user:${payload.userId}`;
 *   await client.join(roomName);
 *   client.emit(UserEvents.JOINED, { userId: payload.userId, room: roomName });
 * }
 * ```
 *
 * @example Frontend - Join on connect
 * ```typescript
 * socket.on("connect", () => {
 *   socket.emit(UserEvents.JOIN, { userId });
 * });
 * socket.on(UserEvents.JOINED, ({ room }) => {
 *   console.log(`Joined personal room: ${room}`);
 * });
 * ```
 */
export const UserEvents = {
  /**
   * Client → Server: Request to join user's personal room
   * Payload: { userId: string }
   */
  JOIN: "user:join",

  /**
   * Client → Server: Request to leave user's personal room
   * Payload: { userId: string }
   */
  LEAVE: "user:leave",

  /**
   * Server → Client: Confirmation of successful user room join
   * Payload: { userId: string, room: string, timestamp: number }
   */
  JOINED: "user:joined",

  /**
   * Server → Client: Confirmation of successful user room leave
   * Payload: { userId: string, room: string, timestamp: number }
   */
  LEFT: "user:left"
} as const;

export const UploadEvents = {
  /**
   * Server → Client: Upload progress update
   * Payload: { attachmentId: string, loaded: number, total: number, percentage: number, timestamp: number }
   * Emitted multiple times during file upload
   */
  PROGRESS: "upload:progress",

  /**
   * Server → Client: File uploaded, now processing/extracting content
   * Payload: { attachmentId: string, filename: string, message?: string, timestamp: number }
   * Emitted after upload completes but before text extraction finishes (for XLSX, PDF, etc.)
   */
  PROCESSING: "upload:processing",

  /**
   * Server → Client: Upload completed successfully
   * Payload: { attachmentId: string, knowledgeId: string, url?: string, thumbnailUrl?: string, timestamp: number }
   * Emitted once when upload and processing complete
   */
  COMPLETED: "upload:completed",

  /**
   * Server → Client: Upload failed
   * Payload: { attachmentId: string, error: { code: string, message: string }, timestamp: number }
   * Emitted when upload fails
   */
  FAILED: "upload:failed",

  /**
   * Server → Client: Upload was cancelled
   * Payload: { attachmentId: string, timestamp: number }
   * Emitted when user cancels upload
   */
  CANCELLED: "upload:cancelled"
} as const;

/**
 * Surfer AI web agent events
 * Real-time streaming of agent thoughts, actions, and results
 *
 * Room pattern: `surfer:{sessionId}`
 *
 * @example Backend - Stream agent events
 * ```typescript
 * this.server.to(`surfer:${sessionId}`).emit(SurferEvents.THOUGHT, {
 *   sessionId,
 *   content: 'Looking for the navigation menu...',
 *   timestamp: Date.now()
 * });
 * ```
 *
 * @example Frontend - Listen for agent stream
 * ```typescript
 * socket.on(SurferEvents.THOUGHT, ({ content }) => {
 *   addThoughtToChat(content);
 * });
 * socket.on(SurferEvents.ACTION, ({ type, description }) => {
 *   addActionToChat(type, description);
 * });
 * ```
 */
export const SurferEvents = {
  /**
   * Client → Server: Start a new Surfer session
   * Payload: SurferStartPayload
   */
  START: "surfer:start",

  /**
   * Client → Server: Send a follow-up message to running session
   * Payload: SurferMessagePayload
   */
  MESSAGE: "surfer:message",

  /**
   * Client → Server: Stop a running session
   * Payload: SurferStopPayload
   */
  STOP: "surfer:stop",

  /**
   * Server → Client: Agent thought/reasoning
   * Payload: SurferThoughtPayload
   */
  THOUGHT: "surfer:thought",

  /**
   * Server → Client: Agent took a browser action
   * Payload: SurferActionPayload
   */
  ACTION: "surfer:action",

  /**
   * Server → Client: Screenshot captured
   * Payload: SurferScreenshotPayload
   */
  SCREENSHOT: "surfer:screenshot",

  /**
   * Server → Client: Script line generated
   * Payload: SurferScriptPayload
   */
  SCRIPT: "surfer:script",

  /**
   * Server → Client: Output data produced
   * Payload: SurferOutputPayload
   */
  OUTPUT: "surfer:output",

  /**
   * Server → Client: Session completed successfully
   * Payload: SurferCompletePayload
   */
  COMPLETE: "surfer:complete",

  /**
   * Server → Client: Error occurred
   * Payload: SurferErrorPayload
   */
  ERROR: "surfer:error",

  /**
   * Server → Client: Session status changed
   * Payload: SurferStatusPayload
   */
  STATUS: "surfer:status"
} as const;

/**
 * Code Interpreter events
 * Real-time streaming of AI agent execution in sandboxed environment
 *
 * These events enable claude.ai-style UI with thinking blocks, tool calls,
 * and streaming text output from the Claude Agent SDK.
 *
 * @example Backend - Stream thinking block
 * ```typescript
 * this.server.to(roomName).emit(CodeInterpreterEvents.BLOCK_START, {
 *   messageId, block: { type: 'thinking', id: 'block-123', content: 'Analyzing...' }
 * });
 * ```
 *
 * @example Frontend - Display thinking block
 * ```typescript
 * socket.on(CodeInterpreterEvents.BLOCK_START, ({ messageId, block }) => {
 *   if (block.type === 'thinking') {
 *     dispatch(addThinkingBlock({ messageId, block }));
 *   }
 * });
 * ```
 */
export const CodeInterpreterEvents = {
  /**
   * Server → Client: New block started (thinking, tool_call, or text)
   * Payload: { messageId: string, block: StreamingBlock }
   */
  BLOCK_START: "code-interpreter:block:start",

  /**
   * Server → Client: Block content updated (for streaming text)
   * Payload: { messageId: string, blockId: string, content: string }
   */
  BLOCK_UPDATE: "code-interpreter:block:update",

  /**
   * Server → Client: Block completed (tool call finished with output)
   * Payload: { messageId: string, blockId: string, output?: string, isError?: boolean }
   */
  BLOCK_COMPLETE: "code-interpreter:block:complete",

  /**
   * Server → Client: Code interpreter execution completed
   * Payload: { messageId: string, timestamp: number }
   */
  EXECUTION_COMPLETE: "code-interpreter:complete",

  /**
   * Server → Client: Code interpreter execution failed
   * Payload: { messageId: string, error: { code: string, message: string }, timestamp: number }
   */
  EXECUTION_ERROR: "code-interpreter:error"
} as const;

/**
 * Resource sync events
 * Real-time notifications when resources are created, updated, or deleted
 * from any source (Web UI, CLI, Public API, Workflow Engine).
 *
 * Clients auto-join the `org:{organizationId}:resources` room on connection.
 * The server emits lightweight invalidation signals (not full entities).
 *
 * @example Backend - Emit resource change
 * ```typescript
 * this.server.to(`org:${orgId}:resources`).emit(ResourceSyncEvents.CHANGED, {
 *   resourceType: "agents", action: "created", entityId: "agent-123",
 *   name: "Customer Support Bot", timestamp: Date.now()
 * });
 * ```
 *
 * @example Frontend - Listen for changes
 * ```typescript
 * socket.on(ResourceSyncEvents.CHANGED, (payload) => {
 *   queryClient.invalidateQueries({ queryKey: ["api", payload.resourceType] });
 *   toast.info(`${payload.resourceType} was ${payload.action}`);
 * });
 * ```
 */
export const ResourceSyncEvents = {
  /** Client → Server: Join the org resources room */
  JOIN: "resource:room:join",
  /** Client → Server: Leave the org resources room */
  LEAVE: "resource:room:leave",
  /** Server → Client: A resource was created, updated, or deleted */
  CHANGED: "resource:changed"
} as const;

/**
 * Resource presence events
 * Track who is currently viewing a specific resource (agent, workflow, deployment).
 * Used for collaborative awareness — "Shady is viewing this agent".
 *
 * @example Frontend - Join presence for a resource
 * ```typescript
 * socket.emit(ResourcePresenceEvents.JOIN, { resourceType: "agents", entityId: "agent-123" });
 * socket.on(ResourcePresenceEvents.UPDATED, ({ viewers }) => {
 *   setOtherViewers(viewers.filter(v => v.userId !== currentUserId));
 * });
 * ```
 */
export const ResourcePresenceEvents = {
  /** Client → Server: Start tracking presence on a resource */
  JOIN: "resource:presence:join",
  /** Client → Server: Stop tracking presence on a resource */
  LEAVE: "resource:presence:leave",
  /** Server → Client: Updated list of viewers for a resource */
  UPDATED: "resource:presence:updated"
} as const;

/**
 * Workflow collaboration events
 * Figma-style real-time collaboration for the workflow editor.
 * Per-workflow room: `workflow:{workflowId}`
 *
 * Cursors, selections, and editing state are ephemeral (never persisted).
 * Structure changes trigger a refetch of the workflow data.
 */
export const WorkflowCollabEvents = {
  /** Client → Server: Join a workflow collaboration session */
  JOIN: "workflow:collab:join",
  /** Client → Server: Leave a workflow collaboration session */
  LEAVE: "workflow:collab:leave",
  /** Client → Server: Cursor position update */
  CURSOR: "workflow:collab:cursor",
  /** Client → Server: Node selection changed */
  SELECTION: "workflow:collab:selection",
  /** Client → Server: Node config panel opened/closed */
  EDITING: "workflow:collab:editing",
  /** Server → Client: Full collaboration state on join */
  STATE: "workflow:collab:state",
  /** Server → Client: Remote cursor position update */
  CURSOR_UPDATED: "workflow:collab:cursor:updated",
  /** Server → Client: Remote selection changed */
  SELECTION_UPDATED: "workflow:collab:selection:updated",
  /** Server → Client: Remote editing state changed */
  EDITING_UPDATED: "workflow:collab:editing:updated",
  /** Server → Client: A viewer joined the workflow */
  VIEWER_JOINED: "workflow:collab:viewer:joined",
  /** Server → Client: A viewer left the workflow */
  VIEWER_LEFT: "workflow:collab:viewer:left",
  /** Server → Client: Workflow structure changed (from any source) */
  STRUCTURE_CHANGED: "workflow:collab:structure:changed"
} as const;

/**
 * Track events — the org-wide board signal.
 *
 * A board over `Track` has no push surface at all today, so it polls. These are
 * the two client verbs for the `org:{organizationId}:tracks` room plus the one
 * server verb that makes polling unnecessary.
 *
 * 🔴 `APPENDED` IS AN INVALIDATION SIGNAL, NOT THE EVENT ROW. It carries ids and
 * a verb and nothing else — no `payload`, no actor, no title. The reason is a
 * trust boundary rather than bandwidth: this room is joined by every member of
 * the organisation, while `track` is a registered `ResourceType` that a
 * permission gate is being built over. A broadcast that carried the row would
 * hand every member the contents of tracks the REST surface is about to refuse
 * them. A client reacts by refetching through that gated route, which is the
 * shape `ResourceSyncEvents.CHANGED` already ships.
 *
 * @example Frontend — stop polling, refetch on the signal
 * ```typescript
 * socket.emit(TrackEvents.JOIN, {});
 * socket.on(TrackEvents.APPENDED, (payload: TrackEventAppendedPayload) => {
 *   queryClient.invalidateQueries({ queryKey: ["api", "tracks"] });
 * });
 * ```
 */
export const TrackEvents = {
  /** Client → Server: Join the org tracks room */
  JOIN: "track:room:join",
  /** Client → Server: Leave the org tracks room */
  LEAVE: "track:room:leave",
  /** Server → Client: The join was accepted, naming the room actually joined */
  JOINED: "track:room:joined",
  /** Server → Client: The leave was accepted */
  LEFT: "track:room:left",
  /** Server → Client: One `TrackEvent` row was appended */
  APPENDED: "track:event:appended"
} as const;

/**
 * All event constants combined
 * Useful for exhaustive type checking
 *
 * ⚠️ THIS IS A VALUE REGISTRY, NOT A KEY REGISTRY. Almost every constant object
 * above declares `JOIN`/`LEAVE`, so a spread collides on those KEYS and the last
 * one silently wins — `WsEvents.JOIN` has meant one arbitrary namespace's join
 * for as long as this object has existed. `WsEventName`, the union of its
 * VALUES, is the half that is sound and the half everything reads. Adding a
 * namespace here is what keeps that union complete; reading `WsEvents.<KEY>` is
 * not a thing to start doing.
 */
export const WsEvents = {
  ...ConnectionEvents,
  ...ChatRoomEvents,
  ...MessageEvents,
  ...ChatMetadataEvents,
  ...OrganizationEvents,
  ...UserEvents,
  ...WorkflowEvents,
  ...InboxEvents,
  ...DeploymentEvents,
  ...OAuthConnectionEvents,
  ...HandshakeEvents,
  ...DocumentEvents,
  ...UploadEvents,
  ...BrowserbaseEvents,
  ...SurferEvents,
  ...CodeInterpreterEvents,
  ...ResourceSyncEvents,
  ...ResourcePresenceEvents,
  ...WorkflowCollabEvents,
  ...TrackEvents
} as const;

/**
 * Type-safe event name union (all possible event names)
 */
export type WsEventName = (typeof WsEvents)[keyof typeof WsEvents];

/**
 * Client -> Server events (what client emits, server listens to)
 */
export const ClientToServerEvents = {
  // Chat room commands
  JOIN_CHAT: ChatRoomEvents.JOIN,
  LEAVE_CHAT: ChatRoomEvents.LEAVE,

  // User room commands
  JOIN_USER: UserEvents.JOIN,
  LEAVE_USER: UserEvents.LEAVE,

  // Message commands
  CREATE_MESSAGE: MessageEvents.CREATE,

  // Surfer commands
  SURFER_START: SurferEvents.START,
  SURFER_MESSAGE: SurferEvents.MESSAGE,
  SURFER_STOP: SurferEvents.STOP,

  // Resource sync room commands
  RESOURCE_ROOM_JOIN: ResourceSyncEvents.JOIN,
  RESOURCE_ROOM_LEAVE: ResourceSyncEvents.LEAVE,

  // Resource presence commands
  RESOURCE_PRESENCE_JOIN: ResourcePresenceEvents.JOIN,
  RESOURCE_PRESENCE_LEAVE: ResourcePresenceEvents.LEAVE,

  // Workflow collaboration commands
  WORKFLOW_COLLAB_JOIN: WorkflowCollabEvents.JOIN,
  WORKFLOW_COLLAB_LEAVE: WorkflowCollabEvents.LEAVE,
  WORKFLOW_COLLAB_CURSOR: WorkflowCollabEvents.CURSOR,
  WORKFLOW_COLLAB_SELECTION: WorkflowCollabEvents.SELECTION,
  WORKFLOW_COLLAB_EDITING: WorkflowCollabEvents.EDITING
} as const;

/**
 * Server -> Client events (what server emits, client listens to)
 */
export const ServerToClientEvents = {
  // Connection
  CONNECTION_READY: ConnectionEvents.READY,
  CONNECTION_ERROR: ConnectionEvents.ERROR,

  // Chat room
  CHAT_ROOM_JOINED: ChatRoomEvents.JOINED,
  CHAT_ROOM_LEFT: ChatRoomEvents.LEFT,

  // Messages
  MESSAGE_CREATED: MessageEvents.CREATED,
  MESSAGE_STREAMING: MessageEvents.STREAMING,
  MESSAGE_COMPLETED: MessageEvents.COMPLETED,
  MESSAGE_FAILED: MessageEvents.FAILED,

  // Chat metadata
  CHAT_TOPIC_UPDATED: ChatMetadataEvents.TOPIC_UPDATED,
  CHAT_STATUS_UPDATED: ChatMetadataEvents.STATUS_UPDATED,
  CHAT_PARTICIPANT_JOINED: ChatMetadataEvents.PARTICIPANT_JOINED,
  CHAT_PARTICIPANT_LEFT: ChatMetadataEvents.PARTICIPANT_LEFT,

  // Workflow
  WORKFLOW_STARTED: WorkflowEvents.STARTED,
  WORKFLOW_PROGRESS: WorkflowEvents.PROGRESS,
  WORKFLOW_COMPLETED: WorkflowEvents.COMPLETED,
  WORKFLOW_FAILED: WorkflowEvents.FAILED,
  WORKFLOW_CANCELLED: WorkflowEvents.CANCELLED,

  // Inbox
  INBOX_MESSAGE_RECEIVED: InboxEvents.NEW_MESSAGE,
  INBOX_UNREAD_COUNT: InboxEvents.UNREAD_COUNT,
  INBOX_CHAT_ASSIGNED: InboxEvents.CHAT_ASSIGNED,
  INBOX_CHAT_RESOLVED: InboxEvents.CHAT_RESOLVED,

  // User (personal)
  USER_JOINED: UserEvents.JOINED,
  USER_LEFT: UserEvents.LEFT,

  // Surfer
  SURFER_THOUGHT: SurferEvents.THOUGHT,
  SURFER_ACTION: SurferEvents.ACTION,
  SURFER_SCREENSHOT: SurferEvents.SCREENSHOT,
  SURFER_SCRIPT: SurferEvents.SCRIPT,
  SURFER_OUTPUT: SurferEvents.OUTPUT,
  SURFER_COMPLETE: SurferEvents.COMPLETE,
  SURFER_ERROR: SurferEvents.ERROR,
  SURFER_STATUS: SurferEvents.STATUS,

  // Resource sync
  RESOURCE_CHANGED: ResourceSyncEvents.CHANGED,

  // Resource presence
  RESOURCE_PRESENCE_UPDATED: ResourcePresenceEvents.UPDATED,

  // Workflow collaboration
  WORKFLOW_COLLAB_STATE: WorkflowCollabEvents.STATE,
  WORKFLOW_COLLAB_CURSOR_UPDATED: WorkflowCollabEvents.CURSOR_UPDATED,
  WORKFLOW_COLLAB_SELECTION_UPDATED: WorkflowCollabEvents.SELECTION_UPDATED,
  WORKFLOW_COLLAB_EDITING_UPDATED: WorkflowCollabEvents.EDITING_UPDATED,
  WORKFLOW_COLLAB_VIEWER_JOINED: WorkflowCollabEvents.VIEWER_JOINED,
  WORKFLOW_COLLAB_VIEWER_LEFT: WorkflowCollabEvents.VIEWER_LEFT,
  WORKFLOW_COLLAB_STRUCTURE_CHANGED: WorkflowCollabEvents.STRUCTURE_CHANGED
} as const;
